import { inject, injectable } from 'inversify';

import { convertSvgClipPathToCSS, convertSvgTransformToCSS } from '@desygner/ui-common-svg-utils';
import {
  Metadata,
  TYPES as PROJECT_SERVICE_TYPES,
  ProjectStoreInjector,
} from '@desygner/ui-features-projects';

import { TYPES } from '../../inversify.types';
import { IViewerStoreInjector } from '../../store';
import { isIOS } from '../../utils/device.util';
import {
  VIDEO_BUTTON_ICONS,
  VIDEO_CONSTANTS,
  VideoAttributes,
  VideoConfiguration,
} from '../../utils/video-embedding.types';
import {
  calculateScaleFactor,
  createVideoControllers,
  parseVideoConfiguration,
  prepareVideoAnchorElement,
  readStoredVideoConfiguration,
  storeVideoDataAttributes,
} from '../../utils/video-embedding.util';

@injectable()
export class VideoManager {
  constructor(
    @inject(TYPES.Store.Viewer) private viewerStore: IViewerStoreInjector,
    @inject(PROJECT_SERVICE_TYPES.ProjectsStore)
    private projectsStore: ProjectStoreInjector
  ) {}

  public currentActiveVideo: HTMLVideoElement | null = null;
  public isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Cache for video-to-page mapping to avoid rebuilding on every call
  private videoToPageMappingCache: Map<string, number> | null = null;
  private cachedProjectId: number | null = null;

  private getVideoId(videoElement: HTMLVideoElement) {
    const videoId = videoElement.getAttribute('id');

    if (!videoId) return;

    return videoId;
  }

  private showPlayButton(videoElement: HTMLVideoElement) {
    const videoId = this.getVideoId(videoElement);

    if (!videoId) return;

    const shadowRoot = videoElement.getRootNode() as ShadowRoot;

    const playButton = shadowRoot.querySelector(
      `[data-video-id="${videoId}"][data-action="play"]`
    ) as HTMLElement;
    const pauseButton = shadowRoot.querySelector(
      `[data-video-id="${videoId}"][data-action="pause"]`
    ) as HTMLElement;

    if (playButton) playButton.style.display = 'flex';

    if (pauseButton) pauseButton.style.display = 'none';
  }

  private showPauseButton(videoElement: HTMLVideoElement) {
    const videoId = this.getVideoId(videoElement);

    if (!videoId) return;

    const shadowRoot = videoElement.getRootNode() as ShadowRoot;

    const playButton = shadowRoot.querySelector(
      `[data-video-id="${videoId}"][data-action="play"]`
    ) as HTMLElement;
    const pauseButton = shadowRoot.querySelector(
      `[data-video-id="${videoId}"][data-action="pause"]`
    ) as HTMLElement;

    if (playButton) playButton.style.display = 'none';

    if (pauseButton) pauseButton.style.display = 'flex';
  }

  /**
   * Build a mapping of video IDs to page IDs from the project metadata
   * Implements caching to avoid rebuilding the mapping on every call
   * @returns Map of video ID (e.g., 'svg_6') to page ID
   */
  private buildVideoToPageMapping(): Map<string, number> {
    const currentProject = this.projectsStore.getState().project.currentProject;

    // Return empty map if no project is loaded
    if (!currentProject || !('metadata' in currentProject)) {
      return new Map<string, number>();
    }

    // Check if cache is valid (same project ID)
    if (this.videoToPageMappingCache !== null && this.cachedProjectId === currentProject.id) {
      return this.videoToPageMappingCache;
    }

    // Cache is invalid, rebuild the mapping
    const mapping = new Map<string, number>();
    const projectMetadata = currentProject.metadata as Metadata | null;
    const mediaByPage = projectMetadata?.media;

    if (mediaByPage) {
      // Build mapping: videoId (svg_X) -> pageId
      Object.entries(mediaByPage).forEach(([pageIdStr, mediaList]) => {
        const pageId = parseInt(pageIdStr, 10);

        mediaList.forEach((media) => {
          mapping.set(media.id, pageId);
        });
      });
    }

    // Update cache
    this.videoToPageMappingCache = mapping;
    this.cachedProjectId = currentProject.id;

    return mapping;
  }

  private handleVideoPause(videoElement: HTMLVideoElement, pageId: number) {
    const svgId = this.getVideoId(videoElement);

    if (!svgId) return;

    this.viewerStore.getState().setMediaList({
      currentTime: videoElement.currentTime,
      svgId,
      pageId,
      totalDuration: videoElement.duration,
      volume: videoElement.volume,
    });

    this.viewerStore.getState().setCurrentPlayingMedia(null);
    videoElement.pause();
    this.showPlayButton(videoElement);
  }

  private handlePauseAllPlayingVideosGlobally(excludeVideo?: HTMLVideoElement) {
    const shadowRoots = document.querySelectorAll('[data-shadow-root-container]');

    if (!shadowRoots) {
      console.error(
        'Cannot find `data-shadow-root-container` dataset attribute on the root.div element'
      );

      return;
    }

    const setMediaList = this.viewerStore.getState().setMediaList;
    const videoToPageMapping = this.buildVideoToPageMapping();

    shadowRoots.forEach((root) => {
      const videos = root.shadowRoot?.querySelectorAll('video');

      videos?.forEach((video) => {
        if (video && !video.paused && video !== excludeVideo) {
          const videoId = video.getAttribute('id');

          if (!videoId) return;

          // Use metadata mapping instead of DOM attributes
          const videoPageId = videoToPageMapping.get(videoId);

          if (!videoPageId) {
            console.error(`Could not determine pageId for video ${videoId} from metadata`);

            return;
          }

          setMediaList({
            currentTime: video.currentTime,
            svgId: videoId,
            pageId: videoPageId,
            totalDuration: video.duration,
            volume: video.volume,
          });

          video.pause();
          this.showPlayButton(video);
        }
      });
    });
  }

  private async handleVideoPlay(videoElement: HTMLVideoElement, pageId: number) {
    this.currentActiveVideo = videoElement;
    const mediaList = this.viewerStore.getState().mediaList;
    const videoId = this.getVideoId(videoElement);

    if (!videoId) return;

    const media = mediaList?.[pageId]?.[videoId];

    if (media) {
      this.viewerStore.getState().setCurrentPlayingMedia(media);
      this.handlePauseAllPlayingVideosGlobally(videoElement);

      if (media.currentTime == undefined) return;

      videoElement.currentTime = media.currentTime;

      try {
        await videoElement.play();
        this.showPauseButton(videoElement);

        this.viewerStore.getState().setCurrentPlayingMedia({
          ...media,
          currentTime: videoElement.currentTime,
        });
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          console.warn(`Video play was aborted for video ${videoId}:`, error.message);
        } else if (error instanceof Error) {
          console.error(`Failed to play video ${videoId}:`, error.message);
        }

        this.showPlayButton(videoElement);
      }

      return;
    }

    const totalDurationInSecs = videoElement.duration;
    const init = {
      currentTime: 0,
      svgId: videoId,
      pageId,
      totalDuration: totalDurationInSecs,
      volume: 1,
    };

    this.viewerStore.getState().setMediaList(init);
    this.viewerStore.getState().setCurrentPlayingMedia(init);

    this.handlePauseAllPlayingVideosGlobally(videoElement);

    try {
      await videoElement.play();
      this.showPauseButton(videoElement);

      this.viewerStore.getState().setCurrentPlayingMedia({
        ...init,
        currentTime: videoElement.currentTime,
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.warn(`Video play was aborted for video ${videoId}:`, error.message);
      } else if (error instanceof Error) {
        console.error(`Failed to play video ${videoId}:`, error.message);
      }

      this.showPlayButton(videoElement);
      this.viewerStore.getState().setCurrentPlayingMedia(null);
    }
  }

  public videoPlayPauseHandler(action: 'play' | 'pause', pageId: number) {
    return (event: React.MouseEvent<HTMLButtonElement> | Event) => {
      const videoElement = event.target as HTMLVideoElement;

      if (!videoElement) return;

      switch (action) {
        case 'pause': {
          videoElement.style.display = 'none'; // Hide the video element when paused
          this.handleVideoPause(videoElement, pageId);
          break;
        }
        case 'play': {
          videoElement.style.display = 'block'; // Ensure the video element is visible when played
          this.handleVideoPlay(videoElement, pageId);
          break;
        }
        default: {
          console.error('Invalid action: ', action);
          break;
        }
      }
    };
  }

  public handleVideoButtonClick = (pageId: number) => {
    return (event: Event) => {
      const target = event.target as HTMLElement;

      const videoControls = target.closest('[data-video-controls]') as HTMLElement;

      const videoId = videoControls?.getAttribute('data-video-controls');

      if (!videoId) return;

      const shadowRoot = videoControls?.getRootNode() as ShadowRoot;
      const videoElement = shadowRoot.querySelector(`video[id="${videoId}"]`) as HTMLVideoElement;

      if (!videoElement) return;

      if (videoControls) {
        if (videoElement.paused) {
          this.handleVideoPlay(videoElement, pageId);
        } else {
          this.handleVideoPause(videoElement, pageId);
        }

        return;
      }

      console.error('No valid button action found.');
    };
  };

  public handleVideoTimeUpdate = (pageId: number) => {
    return (event: Event) => {
      const target = event.target as HTMLVideoElement;

      if (target.tagName !== 'VIDEO') return;

      const videoElement = target as HTMLVideoElement;
      const videoId = this.getVideoId(videoElement);

      if (!videoId || videoElement.paused) return;

      const currentPlayingMedia = this.viewerStore.getState().currentPlayingMedia;

      if (
        currentPlayingMedia &&
        currentPlayingMedia.svgId === videoId &&
        currentPlayingMedia.pageId === pageId
      ) {
        this.viewerStore.getState().setCurrentPlayingMedia({
          ...currentPlayingMedia,
          currentTime: videoElement.currentTime,
        });
      }
    };
  };

  public pauseCurrentActiveVideo() {
    const currentPlayingMedia = this.viewerStore.getState().currentPlayingMedia;

    if (!currentPlayingMedia || !this.currentActiveVideo) {
      return;
    }

    this.handleVideoPause(this.currentActiveVideo, currentPlayingMedia.pageId);
  }

  public pauseOutOfViewVideo({
    pageId,
    rootElement,
  }: {
    pageId: number;
    rootElement: HTMLElement | null;
  }) {
    const VideoAnchorElements = rootElement?.shadowRoot?.querySelectorAll('video');

    VideoAnchorElements?.forEach((video) => {
      const videoId = video.getAttribute('id');

      if (!videoId) return;

      const currentPlayingMedia = this.viewerStore.getState().currentPlayingMedia;
      const setCurrentPlayingMedia = this.viewerStore.getState().setCurrentPlayingMedia;
      const setMediaList = this.viewerStore.getState().setMediaList;

      if (currentPlayingMedia && !video.paused) {
        setMediaList({
          currentTime: video.currentTime,
          svgId: videoId,
          pageId,
          totalDuration: video.duration,
          volume: video.volume,
        });

        setCurrentPlayingMedia(null);
        video.pause();
        this.showPlayButton(video);
      }
    });
  }

  public videoEndedHandler(pageId: number) {
    return (event: Event) => {
      const videoElement = event.target as HTMLVideoElement;

      const svgId = videoElement?.getAttribute('id');

      if (!videoElement || !svgId) return;

      const media = this.viewerStore.getState().mediaList?.[pageId]?.[svgId!];
      const setMediaList = this.viewerStore.getState().setMediaList;
      const setCurrentPlayingMedia = this.viewerStore.getState().setCurrentPlayingMedia;

      if (media) {
        setMediaList({
          ...media,
          currentTime: 0,
        });
      }

      videoElement.currentTime = 0;
      setCurrentPlayingMedia(null);
      this.showPlayButton(videoElement);
    };
  }

  public handleVideoVolumeChange = (value: number) => {
    const setCurrentPlayingMedia = this.viewerStore.getState().setCurrentPlayingMedia;
    const setMediaList = this.viewerStore.getState().setMediaList;
    const currentPlayingMedia = this.viewerStore.getState().currentPlayingMedia;

    if (!currentPlayingMedia || !this.currentActiveVideo) return;

    const svgId = this.getVideoId(this.currentActiveVideo);

    if (!svgId) return;

    const volumeValue = value / 100;

    const updatedMedia = {
      volume: volumeValue,
      pageId: currentPlayingMedia.pageId,
      svgId,
    };

    setMediaList(updatedMedia);
    setCurrentPlayingMedia(updatedMedia);

    // iOS doesn't support volume property, only muted property
    if (isIOS()) {
      // On iOS, treat any value > 0 as unmuted, 0 as muted
      this.currentActiveVideo.muted = volumeValue === 0;
    } else {
      this.currentActiveVideo.volume = volumeValue;
    }
  };

  public handleMuteVolume = () => {
    const setCurrentPlayingMedia = this.viewerStore.getState().setCurrentPlayingMedia;
    const currentPlayingMedia = this.viewerStore.getState().currentPlayingMedia;

    if (!currentPlayingMedia || !this.currentActiveVideo) return;

    const svgId = this.getVideoId(this.currentActiveVideo);

    if (!svgId) return;

    // iOS doesn't support volume property, only muted property
    if (isIOS()) {
      const shouldMute = !this.currentActiveVideo.muted;

      this.currentActiveVideo.muted = shouldMute;

      setCurrentPlayingMedia({
        svgId,
        pageId: currentPlayingMedia.pageId,
        volume: shouldMute ? 0 : 1,
      });
    } else {
      const toggleVolume = currentPlayingMedia?.volume === 0 ? 1 : 0;

      setCurrentPlayingMedia({
        svgId,
        pageId: currentPlayingMedia.pageId,
        volume: toggleVolume,
      });

      this.currentActiveVideo.volume = toggleVolume;
    }
  };

  public getTotalDurationOfAllVideosInPage(pageId: number): string | null {
    const currentProject = this.projectsStore.getState().project.currentProject;

    if (!currentProject) return null;

    const projectMetadata = (
      'metadata' in currentProject ? currentProject.metadata : null
    ) as Metadata | null;

    if (!projectMetadata) return null;

    const mediaList = projectMetadata.media?.[pageId];

    if (!mediaList) return null;

    const totalSeconds = mediaList.reduce((acc: number, media: { duration: number }) => {
      const convertToSecs = media.duration / 1000;

      return acc + (convertToSecs || 0);
    }, 0);

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    const formattedTime =
      hours > 0
        ? `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        : `${minutes}:${seconds.toString().padStart(2, '0')}`;

    return formattedTime;
  }

  /**
   * Extract SVG dimensions from width/height attributes or viewBox
   */
  private extractSvgDimensions(svgElement: SVGSVGElement): {
    width: number;
    height: number;
  } {
    let width = parseFloat(svgElement.getAttribute('width') || '0');
    let height = parseFloat(svgElement.getAttribute('height') || '0');

    if (width === 0 || height === 0) {
      const viewBox = svgElement.getAttribute('viewBox');

      if (viewBox) {
        const viewBoxValues = viewBox.split(/\s+|,/).map(parseFloat);

        if (viewBoxValues.length === 4) {
          width = viewBoxValues[2]; // viewBox format: "minX minY width height"
          height = viewBoxValues[3];
        }
      }
    }

    if (width === 0 || height === 0) {
      const bbox = svgElement.getBoundingClientRect();

      if (bbox.width > 0 && bbox.height > 0) {
        width = bbox.width;
        height = bbox.height;
      }
    }

    return { width, height };
  }

  /**
   * Create a scaled wrapper that matches SVG dimensions and scaling behavior
   */
  private createScaledVideoWrapper(containerElement: HTMLElement): HTMLDivElement {
    const svgElement = containerElement.querySelector('svg');

    if (!svgElement) {
      throw new Error('SVG element not found in container');
    }

    // Get original SVG dimensions with fallbacks
    const { width: originalWidth, height: originalHeight } = this.extractSvgDimensions(svgElement);

    // Guard against invalid dimensions
    if (originalWidth === 0 || originalHeight === 0) {
      console.warn(
        'Unable to determine SVG dimensions (width or height is 0). Using fallback values.',
        { originalWidth, originalHeight }
      );

      // Use reasonable fallback dimensions to avoid division by zero
      const fallbackWidth = originalWidth || 800;
      const fallbackHeight = originalHeight || 600;

      const wrapper = document.createElement('div');

      wrapper.setAttribute('data-video-wrapper', '');
      wrapper.style.position = 'absolute';
      wrapper.style.top = '0';
      wrapper.style.left = '0';
      wrapper.style.width = `${fallbackWidth}px`;
      wrapper.style.height = `${fallbackHeight}px`;
      wrapper.style.pointerEvents = 'none';

      return wrapper;
    }

    // Create wrapper that will scale like the SVG
    const wrapper = document.createElement('div');

    wrapper.setAttribute('data-video-wrapper', '');
    wrapper.style.position = 'absolute';
    wrapper.style.top = '0';
    wrapper.style.left = '0';
    wrapper.style.width = `${originalWidth}px`;
    wrapper.style.height = `${originalHeight}px`;
    wrapper.style.pointerEvents = 'none';

    // Apply the same scaling transform that the SVG effectively gets via CSS
    // The SVG is styled with width: 100%; height: 100%, so we need to match that
    const svgContainer = svgElement.parentElement;

    if (svgContainer) {
      const containerWidth = svgContainer.clientWidth;
      const containerHeight = svgContainer.clientHeight;

      // Guard against division by zero
      if (originalWidth > 0 && originalHeight > 0) {
        const scaleX = containerWidth / originalWidth;
        const scaleY = containerHeight / originalHeight;

        wrapper.style.transform = `scale(${scaleX}, ${scaleY})`;
        wrapper.style.transformOrigin = 'top left';
      }
    }

    return wrapper;
  }

  public createVideoSiblings(containerElement: HTMLElement): HTMLDivElement[] {
    const videoElements: HTMLDivElement[] = [];
    const aElements = containerElement.querySelectorAll('a[data-video-type="mp4"]');

    const pageDimension = this.viewerStore.getState().pageDimension ?? {};
    const scaleFactor = calculateScaleFactor(pageDimension);

    aElements.forEach((anchor) => {
      const config = readStoredVideoConfiguration(anchor);

      if (!config) return;

      const videoContainer = this.createVideoContainer(config, containerElement, scaleFactor);

      videoElements.push(videoContainer);
    });

    return videoElements;
  }

  /**
   * Create a video container with video element and controls for Safari
   */
  private createVideoContainer(
    config: VideoConfiguration,
    containerElement: HTMLElement,
    scaleFactor: number
  ): HTMLDivElement {
    const { attributes, controls, transforms } = config;

    // Create container div
    const containerDiv = document.createElement('div');

    containerDiv.setAttribute('data-video-container', '');
    containerDiv.style.position = 'absolute';
    containerDiv.style.left = `${attributes.x}px`;
    containerDiv.style.top = `${attributes.y}px`;
    containerDiv.style.width = `${attributes.width}px`;
    containerDiv.style.height = `${attributes.height}px`;
    containerDiv.style.pointerEvents = 'none';

    // Apply transform if exists
    if (transforms.transform) {
      const cssTransform = convertSvgTransformToCSS(transforms.transform);

      containerDiv.style.transform = cssTransform;
    }

    // Create and append video element
    const video = this.createVideoElement(attributes, transforms.clipPath, containerElement);

    containerDiv.appendChild(video);

    // Create and append controls
    const controlsDiv = createVideoControllers(attributes.id, controls, scaleFactor);

    containerDiv.appendChild(controlsDiv);

    return containerDiv;
  }

  /**
   * Create video element with source
   */
  private createVideoElement(
    attributes: VideoAttributes,
    clipPath: string,
    containerElement: HTMLElement
  ): HTMLVideoElement {
    const video = document.createElement('video');

    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('crossorigin', 'anonymous');
    video.setAttribute('preload', 'metadata');
    video.setAttribute('width', String(attributes.width));
    video.setAttribute('height', String(attributes.height));
    video.setAttribute('id', attributes.id);
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.display = 'none'; // we hide video initially and show the poster only, show on click

    if (clipPath) {
      const cssClipPath = convertSvgClipPathToCSS({
        clipPathAttr: clipPath,
        containerElement,
        video: {
          x: attributes.x,
          y: attributes.y,
          width: attributes.width,
          height: attributes.height,
        },
      });

      if (cssClipPath) {
        video.style.clipPath = cssClipPath;
      }
    }

    const source = document.createElement('source');

    source.setAttribute('src', attributes.sourceUrl);
    source.setAttribute('type', `video/${attributes.videoType}`);
    video.appendChild(source);

    return video;
  }

  /**
   * Setup video siblings for Safari (creates DOM elements only, no event listeners)
   */
  public setupVideoSiblingsIfNeeded(containerElement: HTMLElement, contentHash: string): void {
    if (!containerElement) return;

    // Check if videos are already set up for this content
    const currentHash = containerElement.getAttribute('data-videos-setup');

    if (currentHash === contentHash) {
      // Videos already exist for this content, no need to recreate
      return;
    }

    // Content has changed, remove old videos and wrapper
    const existingWrapper = containerElement.querySelector('[data-video-wrapper]');

    if (existingWrapper) {
      existingWrapper.remove();
    }

    // Create scaled wrapper that matches SVG dimensions
    const videoWrapper = this.createScaledVideoWrapper(containerElement);

    // Create and append video siblings to the wrapper
    const videoSiblings = this.createVideoSiblings(containerElement);

    videoSiblings.forEach((videoElement) => {
      videoWrapper.appendChild(videoElement);
    });

    // Append wrapper to container
    containerElement.appendChild(videoWrapper);

    // Mark container as setup for this content
    containerElement.setAttribute('data-videos-setup', contentHash);
  }

  /**
   * Create foreignObject element with embedded video
   */
  private createForeignObjectVideo(config: VideoConfiguration): SVGForeignObjectElement {
    const { attributes, controls } = config;
    const pageDimension = this.viewerStore.getState().pageDimension ?? {};
    const scaleFactor = calculateScaleFactor(pageDimension);

    const foreignObjectElement = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'foreignObject'
    );

    foreignObjectElement.setAttribute('x', String(attributes.x));
    foreignObjectElement.setAttribute('y', String(attributes.y));
    foreignObjectElement.setAttribute('width', String(attributes.width));
    foreignObjectElement.setAttribute('height', String(attributes.height));

    const scaleTransform = `transform: scale(${scaleFactor});`;
    const videoControlsStyle = `position: absolute; left: ${controls.x}px; top: ${controls.y}px; width: ${controls.width}px; height: ${controls.height}px; cursor: pointer; display: flex; align-items: center; justify-content: center; box-sizing: border-box;`;
    const buttonStyle = `width: ${VIDEO_CONSTANTS.DEFAULT_BUTTON_SIZE}px; height: ${VIDEO_CONSTANTS.DEFAULT_BUTTON_SIZE}px; ${scaleTransform}`;

    foreignObjectElement.innerHTML = `
      <div data-video-container xmlns="http://www.w3.org/1999/xhtml">
        <video playsinline webkit-playsinline crossorigin="anonymous" preload="metadata" width="${attributes.width}" height="${attributes.height}" id="${attributes.id}">
          <source src="${attributes.sourceUrl}" type="video/${attributes.videoType}" />
        </video>
        <div data-video-controls="${attributes.id}" style="${videoControlsStyle}">
          <button data-action="play" data-video-id="${attributes.id}" data-video="${attributes.id}" style="${buttonStyle}">
            ${VIDEO_BUTTON_ICONS.PLAY}
          </button>
          <button data-action="pause" data-video-id="${attributes.id}" data-video="${attributes.id}" style="${buttonStyle}">
            ${VIDEO_BUTTON_ICONS.PAUSE}
          </button>
        </div>
      </div>
    `;

    return foreignObjectElement;
  }

  /**
   * Non-Safari browsers: embed videos using foreignObject
   */
  private embedVideosUsingForeignObject(element: HTMLElement): HTMLElement {
    const aElements = element.querySelectorAll('a');

    aElements.forEach((anchor) => {
      const videoType = anchor.getAttribute('data-video-type');

      if (videoType !== 'mp4') return;

      const config = parseVideoConfiguration(anchor);

      if (!config) return;

      const foreignObject = this.createForeignObjectVideo(config);

      anchor.append(foreignObject);
      prepareVideoAnchorElement(anchor);
    });

    return element;
  }

  /**
   * Safari-specific video embedding: stores video config as data attributes
   */
  private embedVideosForSafari(element: HTMLElement): HTMLElement {
    const aElements = element.querySelectorAll('a');

    aElements.forEach((anchor) => {
      const videoType = anchor.getAttribute('data-video-type');

      if (videoType !== 'mp4') return;

      const config = parseVideoConfiguration(anchor);

      if (!config) return;

      storeVideoDataAttributes(anchor, config);
      prepareVideoAnchorElement(anchor);
    });

    return element;
  }

  public embedNonYoutubeVideos(element: HTMLElement) {
    if (this.isSafari) {
      return this.embedVideosForSafari(element);
    }

    return this.embedVideosUsingForeignObject(element);
  }
}
